/**
 * The view editor's Data Preview, as AgencyAnalytics': the unsaved view run
 * live for a period (Last 30 Days first), "Showing N of N Rows", a TOTALS
 * row, then one row per dimension value.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { formatValue, PRESET_LABELS } from '../reports/format.ts';
import { reportsApi, type WidgetResult } from '../reports/reports-api.ts';
import type { ViewDefinition } from './custom-api.ts';
import { FORMAT_OF } from './with-custom.ts';

const PERIODS = [
  'LAST_7_DAYS',
  'LAST_30_DAYS',
  'LAST_MONTH',
  'THIS_MONTH',
  'LAST_QUARTER',
  'THIS_YEAR',
];
const DELAY_MS = 500;
const PREVIEW_ROWS = 100;

export function ViewPreview(props: {
  organisationId: string;
  clientId: string;
  timezone: string;
  definition: ViewDefinition;
}): ReactNode {
  const [preset, setPreset] = useState('LAST_30_DAYS');
  const [result, setResult] = useState<WidgetResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const { definition } = props;
  const signature = JSON.stringify([definition, preset]);
  useEffect(() => {
    if (definition.sources.length === 0 || definition.columns.length === 0) {
      setResult(null);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      reportsApi
        .query(props.organisationId, {
          business_unit_id: props.clientId,
          mode: 'LIVE',
          timezone: props.timezone,
          range: { preset },
          preview_view: definition,
          widgets: [
            {
              id: 'preview',
              source: 'view:preview',
              metrics: definition.columns.map((column) => column.id),
              limit: PREVIEW_ROWS,
            },
          ],
        })
        .then((data) => {
          if (cancelled) return;
          setResult(data.results[0] ?? null);
          setProblem(null);
        })
        .catch(
          (error: unknown) =>
            !cancelled &&
            setProblem(error instanceof Error ? error.message : 'The preview could not run.'),
        );
    }, DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // The signature holds every input of the preview.
  }, [signature, props.organisationId, props.clientId, props.timezone]);

  const cell = (
    value: number | null | undefined,
    dataType: ViewDefinition['columns'][number]['data_type'],
  ) => formatValue(value ?? null, FORMAT_OF[dataType]);
  const rows = result?.rows ?? [];
  return (
    <section className="cx-section" aria-labelledby="vp-title">
      <div className="cx-row">
        <h3 id="vp-title">Data Preview</h3>
        <span className="ag-spacer" />
        <select
          aria-label="Preview period"
          value={preset}
          onChange={(event) => setPreset(event.target.value)}
        >
          {PERIODS.map((value) => (
            <option key={value} value={value}>
              {PRESET_LABELS[value]}
            </option>
          ))}
        </select>
      </div>
      {problem && (
        <p className="kp-problem" role="alert">
          {problem}
        </p>
      )}
      {result?.state === 'ERROR' && <p className="kp-problem">{result.error}</p>}
      {result && result.state !== 'ERROR' && (
        <div className="ag-table-card cx-preview">
          <p className="ag-table-meta">
            Showing {rows.length} of {result.total_rows ?? rows.length} Rows
          </p>
          <table className="ag-table">
            <thead>
              <tr>
                <th scope="col">{definition.dimension.name || 'Name'}</th>
                {definition.columns.map((column) => (
                  <th scope="col" key={column.id} className="ag-num">
                    {column.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="cx-totals">
                <th scope="row">TOTALS</th>
                {definition.columns.map((column) => (
                  <td key={column.id} className="ag-num">
                    {cell(
                      result.metrics.find((metric) => metric.key === column.id)?.value,
                      column.data_type,
                    )}
                  </td>
                ))}
              </tr>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  {definition.columns.map((column) => (
                    <td key={column.id} className="ag-num">
                      {cell(row.values[column.id], column.data_type)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
